import React, { useId } from 'react';
import { Check } from 'lucide-react';

/**
 * Shared "I agree to Terms + Privacy" consent block.
 * Used on every form that collects personal information / forwards
 * enquiries to third-party businesses on the platform.
 *
 * Usage:
 *   const [termsAccepted, setTermsAccepted] = useState(false);
 *   ...
 *   <TermsAcceptCheckbox checked={termsAccepted} onChange={setTermsAccepted} testId="my-form-terms" />
 *   ...
 *   if (!termsAccepted) { setError('Please accept the Terms and Privacy Policy.'); return; }
 *
 * 2026-02-20: rewritten to fix iOS Safari tap bug. Previous implementation
 * used a native <input type="checkbox"> nested inside a flex <label> - a
 * combo iOS Safari intermittently fails to associate (label tap doesn't
 * fire onChange). Now uses an htmlFor/id pair AND a custom div-button as
 * the visible check, with a 44px minimum tap target.
 */
const TermsAcceptCheckbox = ({ checked, onChange, testId = 'terms-accept', className = '' }) => {
  const id = useId();
  const cbId = `${id}-cb`;

  const toggle = () => onChange(!checked);

  // Stop link clicks inside the text from bubbling up to the row toggle.
  const stop = (e) => e.stopPropagation();

  return (
    <div
      className={`flex items-start gap-3 text-[12.5px] leading-[1.55] text-[#535357] ${className}`}
      data-testid={`${testId}-wrap`}
    >
      {/* Visually-hidden native input keeps form semantics + a11y intact. */}
      <input
        id={cbId}
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
        data-testid={testId}
      />
      {/* Custom 44px-min tap target. Apple HIG calls for 44pt; we hit 40px
          for the inner box + 4px padding ring = 48px. The visible 18px box
          sits centered inside. */}
      <button
        type="button"
        role="checkbox"
        aria-checked={!!checked}
        aria-labelledby={`${cbId}-label`}
        onClick={toggle}
        // iOS sometimes swallows the synthetic click on the first tap after
        // focus; bind touchend as a belt-and-braces fallback. We
        // preventDefault to avoid a "ghost click" double-fire.
        onTouchEnd={(e) => { e.preventDefault(); toggle(); }}
        className={`mt-[1px] inline-flex items-center justify-center shrink-0 rounded-md border transition-colors ${
          checked
            ? 'bg-[#1d8efe] border-[#1d8efe]'
            : 'bg-white border-[#86868b] hover:border-[#1d8efe]'
        }`}
        style={{
          width: 22,
          height: 22,
          // Pad the click area out to 44x44 without changing the visible box.
          // The negative margin keeps the visual layout where it was.
          padding: 0,
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
        }}
        data-testid={`${testId}-button`}
      >
        {checked && <Check size={14} strokeWidth={3} className="text-white" />}
      </button>
      <span
        id={`${cbId}-label`}
        onClick={toggle}
        // Make the entire text block a tap target too - iOS users naturally
        // tap the words, not the box.
        className="cursor-pointer select-none"
        style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
      >
        I agree to BuildPilot&apos;s{' '}
        <a
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          onClick={stop}
          onTouchEnd={stop}
          className="text-[#1d8efe] hover:underline font-medium"
        >
          Terms
        </a>
        {' '}and{' '}
        <a
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          onClick={stop}
          onTouchEnd={stop}
          className="text-[#1d8efe] hover:underline font-medium"
        >
          Privacy Policy
        </a>
        , and acknowledge that relevant parts of my brief may be shared with suitable businesses BuildPilot introduces me to.
        {' '}I understand BuildPilot <strong>introduces only</strong> - it does not warrant, endorse, supervise or guarantee any business and is not liable for their work.
      </span>
    </div>
  );
};

export default TermsAcceptCheckbox;
